import { decodeTime } from 'ulid';

import { createEventFactory } from '$app/event-factory';
import type { EventFactory } from '$app/event-factory';
import { getDeviceId } from '$infra/device';
import { createIdbEventStore } from '$infra/event-store.idb';
import type { EventStore } from '$infra/event-store';
import { createIdbScratchStore } from '$infra/scratch-store';
import type { ScratchStore } from '$infra/scratch-store';
import { systemClock } from '$infra/clock';
import type { Clock } from '$infra/clock';

/**
 * Everything the application layer needs, assembled once.
 *
 * **This never blocks first paint.** Spec §15 requires the Catch button to be
 * tappable in under a second, and architecture §2.7 makes that precise: the
 * button is interactive *before any store read completes*. So the UI mounts
 * first and awaits this afterwards; nothing in the core action waits on
 * IndexedDB.
 */
export interface AppContext {
  store: EventStore;
  scratch: ScratchStore;
  clock: Clock;
  deviceId: string;
  events: EventFactory;
}

export async function bootstrap(clock: Clock = systemClock): Promise<AppContext> {
  const store = createIdbEventStore();
  const scratch = createIdbScratchStore();
  const deviceId = await getDeviceId(scratch);

  // Seed the id floor from the log's newest event. Without this, a device whose
  // clock moved backwards while the app was closed would mint ids that sort
  // before events already written — replaying history out of order and hiding
  // new events from a sync watermark. See event-factory.ts.
  const lastId = await store.lastId();
  const floorMs = lastId === null ? 0 : decodeTime(lastId);

  const events = createEventFactory({ clock, deviceId, floorMs });

  return { store, scratch, clock, deviceId, events };
}

/**
 * Ask the browser not to evict our storage.
 *
 * Called after onboarding rather than at first launch: the prompt some browsers
 * show is meaningless before the user has anything to lose, and asking early
 * spends a permission we only get to request convincingly once. Spec §2.3 —
 * if this is denied, the export prompt is how the user protects themselves.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}
